import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from 'redux-form';

import CurrencyInputField from 'inquiry/components/CurrencyInputField';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { InputCurrencyComponent } from 'lib/interfaces/ui-components';
import isEmpty from 'lib/utils/formValidation/isEmpty';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface InputCurrency {
  component: InputCurrencyComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
}

const InputCurrency: FC<InputCurrency> = ({ component, inquiry, actionsApi }: InputCurrency) => {
  const {
    disabled,
    hidden,
    label,
    optional,
    placeholder,
    prefill,
    validations = [],
    'currency-code': currencyCode,
  } = component.attributes;
  const formField = component.name;
  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const validators = useValidators({ hidden, optional, validations });

  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    } else if (!isHidden && !isEmpty(prefill)) {
      actionsApi.setField(formField, prefill);
    }

    // Including actionsApi here results in value resetting on
    // prefill and blur
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  const { i18n } = useTranslation();

  // if the current browser locale matches the selected language locale,
  // use the browser locale, otherwise use the language locale
  // We prefer to use the device language over i18n.language where possible
  // because i18n.language casts the device locale to the closest supported
  // translation locale, which may alter the region. (e.g. es-ES will become es-MX)
  // This has significant impact on the currency localization.
  // In cases where the Persona translation locale language matches the device language,
  // we want to use the device's region.
  const locale =
    navigator.language.split('-')[0] === i18n.language.split('-')[0]
      ? navigator.language
      : i18n.language;

  if (isHidden) {
    return null;
  }

  return (
    <Field
      name={formField}
      id={formField}
      component={CurrencyInputField}
      disabled={apply<boolean>(disabled)}
      validate={validators}
      label={label}
      placeholder={placeholder}
      type='text'
      inputmode='decimal'
      styles={component.styles}
      intlConfig={{ locale, currency: currencyCode }}
    />
  );
};

export default InputCurrency;
